import { useEffect, useRef, useState } from 'react'

import { cabecerasAuth, urlImagen } from '../api/cliente'
import { almacen } from './almacen'

/**
 * Descarga y cacheo de imagenes (portadas y paginas).
 *
 * No se usa <img src> directamente por dos razones: la API exige
 * autenticacion y el navegador no manda la cabecera en peticiones de
 * imagen, y pasar por fetch es lo que permite guardar la pagina para
 * verla sin conexion.
 *
 * Hay dos niveles de cache. El de memoria guarda los object URL ya creados
 * y responde de forma sincrona: es lo que hace que pasar de pagina en el
 * lector sea instantaneo, porque la siguiente ya viene precargada y no hay
 * que tocar el disco. El de IndexedDB sobrevive al cierre de la app.
 */

// Suwayomi tiene maxSourcesInParallel = 6. Si la rejilla dispara cincuenta
// peticiones a la vez, el servidor las encola y varias acaban expirando.
const CONCURRENCIA = 4
const REINTENTOS = 2

// Suficiente para el capitulo que se esta leyendo y la rejilla visible.
// Cada entrada es un object URL, no la imagen: el coste real lo lleva el
// navegador, y al soltarlo libera la memoria.
const MAX_MEMORIA = 60

interface Entrada {
  blob: Blob
  url: string
}

// Se guarda tambien el blob: el object URL ya mantiene vivo el binario, asi
// que la referencia no cuesta memoria extra, y evita tener que releer del
// disco cuando algo necesita el contenido y no la URL.
const memoria = new Map<string, Entrada>()

let enCurso = 0
const cola: (() => void)[] = []

// Peticiones ya lanzadas, para no pedir dos veces la misma imagen cuando el
// lector la precarga y el usuario llega a ella a la vez.
const enVuelo = new Map<string, Promise<Entrada | null>>()

/**
 * La pagina que se esta mirando entra por delante de la cola.
 *
 * Sin esto, pasar paginas deprisa encolaba decenas de precargas y la imagen
 * que el usuario esperaba quedaba la ultima: parecia que el lector se
 * quedaba pegado en una pagina.
 */
function turno(prioritario: boolean): Promise<void> {
  if (enCurso < CONCURRENCIA) {
    enCurso++
    return Promise.resolve()
  }
  return new Promise((resolver) => {
    if (prioritario) cola.unshift(resolver)
    else cola.push(resolver)
  })
}

function liberar() {
  const siguiente = cola.shift()
  if (siguiente) siguiente()
  else enCurso--
}

function esperar(ms: number) {
  return new Promise((resolver) => setTimeout(resolver, ms))
}

export function claveImagen(ruta: string): string {
  return `img:${ruta}`
}

function tomar(ruta: string): Entrada | null {
  const entrada = memoria.get(ruta)
  if (!entrada) return null
  // Al releerla se mueve al final para que no la expulse el recorte
  memoria.delete(ruta)
  memoria.set(ruta, entrada)
  return entrada
}

/** URL ya disponible en memoria, o null. Sincrono a proposito. */
export function urlEnMemoria(ruta: string): string | null {
  return tomar(ruta)?.url ?? null
}

function recordar(ruta: string, blob: Blob): Entrada {
  const existente = tomar(ruta)
  if (existente) return existente

  const entrada: Entrada = { blob, url: URL.createObjectURL(blob) }
  memoria.set(ruta, entrada)

  while (memoria.size > MAX_MEMORIA) {
    const vieja = memoria.keys().next().value as string | undefined
    if (vieja === undefined) break
    const entradaVieja = memoria.get(vieja)
    if (entradaVieja) URL.revokeObjectURL(entradaVieja.url)
    memoria.delete(vieja)
  }
  return entrada
}

/**
 * Saca estas imagenes de la cache, en memoria y en disco.
 *
 * Es para cuando la ruta sigue valiendo pero su contenido ya no: el capitulo
 * se descargo a medias y la fuente sirve ahora otra cosa en la misma pagina.
 *
 * A proposito **no** se revoca el object URL, al reves que el recorte por
 * tamano: puede haber un <img> pintandolo ahora mismo y revocarlo lo dejaria
 * roto sin que nada volviera a pedirlo. Son unas pocas paginas y el navegador
 * las suelta al cerrar; quien tenga que releerlas ya no las encuentra aqui.
 */
export async function olvidarImagenes(rutas: string[]): Promise<void> {
  for (const ruta of rutas) {
    memoria.delete(ruta)
    enVuelo.delete(ruta)
    await almacen.borrar(claveImagen(ruta))
  }
}

/**
 * Deja la imagen lista en memoria y devuelve su URL.
 *
 * El guardado en IndexedDB no se espera: con paginas de 300 KB en iOS
 * escribir tarda lo suyo, y no hay razon para retrasar el pintado cuando
 * el blob ya esta descargado.
 */
async function traer(ruta: string, prioritario: boolean): Promise<Entrada | null> {
  const clave = claveImagen(ruta)
  const guardada = await almacen.leer(clave)
  if (guardada) return recordar(ruta, guardada)

  await turno(prioritario)
  try {
    for (let intento = 0; intento <= REINTENTOS; intento++) {
      try {
        const respuesta = await fetch(urlImagen(ruta), { headers: cabecerasAuth() })
        if (respuesta.ok) {
          const blob = await respuesta.blob()
          void almacen.guardar(clave, blob).catch(() => {
            // Sin espacio o almacenamiento denegado: se sigue leyendo igual,
            // solo se pierde la copia para offline.
          })
          return recordar(ruta, blob)
        }
        // Un 404 no se arregla reintentando; un 429 o un 5xx si
        if (respuesta.status === 404) return null
      } catch {
        // Corte de red: entra en el reintento
      }
      if (intento < REINTENTOS) await esperar(700 * (intento + 1))
    }
    return null
  } finally {
    liberar()
  }
}

export function obtenerEntrada(ruta: string, prioritario = false): Promise<Entrada | null> {
  const enMemoria = tomar(ruta)
  if (enMemoria) return Promise.resolve(enMemoria)

  // Si ya se estaba pidiendo (por ejemplo, la precarga se adelanto), se
  // reaprovecha en vez de lanzar una segunda peticion identica.
  const existente = enVuelo.get(ruta)
  if (existente) return existente

  const promesa = traer(ruta, prioritario).finally(() => enVuelo.delete(ruta))
  enVuelo.set(ruta, promesa)
  return promesa
}

export type EstadoImagen = 'espera' | 'cargando' | 'lista' | 'fallo'

interface Opciones {
  /** Si es false, no se descarga hasta que pase a true (carga perezosa). */
  activo?: boolean
  /** La imagen que el usuario esta esperando ver: adelanta en la cola. */
  prioritario?: boolean
}

export function useImagen(
  ruta: string | null,
  { activo = true, prioritario = false }: Opciones = {},
) {
  // Si ya esta en memoria se entrega en el primer render, sin parpadeo ni
  // espera: es lo que hace instantaneo el cambio de pagina.
  const [url, setUrl] = useState<string | null>(() => (ruta ? urlEnMemoria(ruta) : null))
  const [estado, setEstado] = useState<EstadoImagen>(url ? 'lista' : 'espera')
  const [intento, setIntento] = useState(0)

  useEffect(() => {
    if (!ruta || !activo) return

    const inmediata = urlEnMemoria(ruta)
    if (inmediata) {
      setUrl(inmediata)
      setEstado('lista')
      return
    }

    let cancelado = false
    setEstado('cargando')

    void obtenerImagen(ruta, prioritario).then((nueva) => {
      if (cancelado) return
      if (!nueva) {
        setEstado('fallo')
        return
      }
      setUrl(nueva)
      setEstado('lista')
    })

    return () => {
      cancelado = true
      // No se revoca aqui: los object URL son propiedad de la cache de
      // memoria, que los suelta al expulsarlos. Revocarlos al desmontar
      // rompia la imagen si el mismo blob seguia en uso en otra vista.
    }
  }, [ruta, activo, prioritario, intento])

  return { url, estado, reintentar: () => setIntento((n) => n + 1) }
}

/**
 * Hook de visibilidad, para no descargar lo que no se ve.
 *
 * En una rejilla de cincuenta series solo se piden las portadas que estan
 * en pantalla o a punto de entrar.
 */
export function useVisible<T extends Element>(margen = '300px') {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const elemento = ref.current
    if (!elemento || visible) return

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) {
          setVisible(true)
          observador.disconnect()
        }
      },
      { rootMargin: margen },
    )
    observador.observe(elemento)
    return () => observador.disconnect()
  }, [visible, margen])

  return { ref, visible }
}

/**
 * Baja paginas por adelantado mientras se lee la actual.
 *
 * Recibe `seguir` para poder abandonar a mitad: si el usuario sigue pasando
 * paginas, las precargas de hace tres pantallas ya no interesan y solo
 * estorbarian en la cola.
 */
export async function precargar(rutas: string[], seguir: () => boolean): Promise<void> {
  for (const ruta of rutas) {
    if (!seguir()) return
    await obtenerImagen(ruta)
  }
}

export async function obtenerImagen(ruta: string, prioritario = false): Promise<string | null> {
  return (await obtenerEntrada(ruta, prioritario))?.url ?? null
}

/**
 * Solo para la deteccion de formato, que necesita medir el bitmap.
 *
 * Sale de la cache de memoria y no de IndexedDB a proposito: el guardado en
 * disco no se espera, asi que releerlo podria devolver nada.
 */
export async function descargarImagen(ruta: string): Promise<Blob | null> {
  return (await obtenerEntrada(ruta))?.blob ?? null
}

/**
 * Los primeros bytes de una imagen, sin llegar a bajarla entera.
 *
 * La deteccion de formato solo necesita la cabecera, pero hay fuentes que
 * sirven las paginas como JPEG de casi un mega: medir tres paginas costaba
 * dos megas de descarga para leer veinte bytes, y esa transferencia le
 * quitaba el sitio a la pagina que el usuario estaba esperando.
 *
 * Se corta el stream en cuanto hay suficiente y se aborta la peticion, con
 * lo que el servidor deja de enviar. Se hace aqui y no con la cabecera
 * `Range` porque Suwayomi la ignora: responde 200 con el fichero completo.
 *
 * El trozo no se guarda en ninguna cache. Es una imagen incompleta, y
 * dejarla donde el lector busca las suyas la mostraria rota.
 */
export async function cabeceraImagen(ruta: string, bytes: number): Promise<DataView | null> {
  // Si ya esta bajada entera no hay nada que pedir
  const enMemoria = tomar(ruta)
  if (enMemoria) return new DataView(await enMemoria.blob.slice(0, bytes).arrayBuffer())

  const guardada = await almacen.leer(claveImagen(ruta))
  if (guardada) return new DataView(await guardada.slice(0, bytes).arrayBuffer())

  await turno(false)
  const control = new AbortController()
  try {
    const respuesta = await fetch(urlImagen(ruta), {
      headers: cabecerasAuth(),
      signal: control.signal,
    })
    if (!respuesta.ok || !respuesta.body) return null

    const lector = respuesta.body.getReader()
    const trozos: Uint8Array[] = []
    let total = 0
    while (total < bytes) {
      const { done, value } = await lector.read()
      if (done || !value) break
      trozos.push(value)
      total += value.length
    }
    void lector.cancel().catch(() => {
      // Ya se ha leido lo que hacia falta; que el cierre falle da igual
    })

    if (total === 0) return null
    const buffer = new Uint8Array(total)
    let pos = 0
    for (const trozo of trozos) {
      buffer.set(trozo, pos)
      pos += trozo.length
    }
    return new DataView(buffer.buffer)
  } catch {
    return null
  } finally {
    control.abort()
    liberar()
  }
}
